import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import api from '../api/axios';
import { useBoilerSocket } from '../hooks/useSocket';

const OFFLINE_MS = 2 * 60 * 1000;

const PERIODS = [
  { key: '1h',  label: '1 ч',   ms: 3600000,   bucketMs: 60000,   gapMs: 3 * 60000 },
  { key: '6h',  label: '6 ч',   ms: 21600000,  bucketMs: 300000,  gapMs: 15 * 60000 },
  { key: '24h', label: '24 ч',  ms: 86400000,  bucketMs: 900000,  gapMs: 45 * 60000 },
  { key: '7d',  label: '7 дн',  ms: 604800000, bucketMs: 3600000, gapMs: 3 * 3600000 },
];

function parseRows(rows, period) {
  const show24h = period === '24h' || period === '7d';
  const gapMs = PERIODS.find(p => p.key === period)?.gapMs ?? 3 * 60000;
  const points = [];
  for (let i = 0; i < rows.length; i++) {
    if (i > 0) {
      const diff = new Date(rows[i].recorded_at) - new Date(rows[i - 1].recorded_at);
      if (diff > gapMs) points.push({ time: '', temperature: null, temperature2: null, pressure: null, pressureMin: null, pressureMax: null, gap: true });
    }
    const d = new Date(rows[i].recorded_at);
    const time = show24h
      ? d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    points.push({
      time,
      temperature: rows[i].temperature !== null ? parseFloat(rows[i].temperature) : null,
      temperature2: rows[i].temperature2 !== null && rows[i].temperature2 !== undefined ? parseFloat(rows[i].temperature2) : null,
      pressure: rows[i].pressure !== null ? parseFloat(rows[i].pressure) : null,
      pressureMin: rows[i].pressure_min !== null && rows[i].pressure_min !== undefined ? parseFloat(rows[i].pressure_min) : null,
      pressureMax: rows[i].pressure_max !== null && rows[i].pressure_max !== undefined ? parseFloat(rows[i].pressure_max) : null,
    });
  }
  return points;
}

function StatCard({ label, value, unit, warn, offline }) {
  return (
    <div className={`bg-slate-800 rounded-2xl p-4 border ${warn ? 'border-red-500/50' : 'border-slate-700'}`}>
      <p className="text-slate-400 text-sm mb-1">{label}</p>
      <p className={`text-3xl font-bold ${warn ? 'text-red-400' : 'text-white'}`}>
        {offline || value === null || value === undefined
          ? <span className="text-slate-500 text-2xl">—</span>
          : <>{parseFloat(value).toFixed(2)}<span className="text-lg font-normal text-slate-400 ml-1">{unit}</span></>}
      </p>
    </div>
  );
}

const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', fontSize: 12 };

function BoilerChart({ data, dataKey, color, unit, label, gradientId, secondaryDataKey, secondaryLabel, secondaryColor = '#f59e0b' }) {
  const hasData = data.some(d => d[dataKey] !== null || (secondaryDataKey && d[secondaryDataKey] !== null));
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-4 mb-4">
      <p className="text-sm font-semibold text-slate-300 mb-3">{label}</p>
      {!hasData ? (
        <div className="h-36 flex items-center justify-center text-slate-500 text-sm">Нет данных</div>
      ) : (
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart data={data} margin={{ top: 72, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.2} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis
              dataKey="time"
              tick={{ fill: '#64748b', fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={40}
              domain={[(dataMin) => Math.max(0, parseFloat((dataMin - Math.abs(dataMin) * 0.1 - 0.1).toFixed(2))), (dataMax) => parseFloat((dataMax + Math.abs(dataMax) * 0.1 + 0.1).toFixed(2))]}
              tickFormatter={(v) => v}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              labelStyle={{ color: '#94a3b8' }}
              position={{ x: 4, y: 4 }}
              wrapperStyle={{ zIndex: 10, pointerEvents: 'none' }}
              formatter={(v, name) => {
                const seriesLabel = name === secondaryDataKey ? secondaryLabel : label;
                return (v !== null && v !== undefined && !isNaN(v))
                  ? [`${parseFloat(v).toFixed(2)} ${unit}`, seriesLabel]
                  : ['Нет данных', seriesLabel];
              }}
            />
            <Area
              type="monotoneX"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 3, fill: color }}
              connectNulls={false}
              isAnimationActive={false}
            />
            {secondaryDataKey && (
              <Area
                type="monotoneX"
                dataKey={secondaryDataKey}
                stroke={secondaryColor}
                strokeWidth={2}
                strokeDasharray="5 4"
                fill="none"
                dot={false}
                activeDot={{ r: 3, fill: secondaryColor }}
                connectNulls={false}
                isAnimationActive={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function BoilerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [boiler, setBoiler] = useState(null);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [thresholds, setThresholds] = useState({ temp_min: 0, temp_max: 90, temp2_min: 0, temp2_max: 90, pressure_min: 0.5, pressure_max: 3, notify_offline: true, notify_online: true, notify_thresholds: true });
  const [saving, setSaving] = useState(false);
  const [thresholdSaveState, setThresholdSaveState] = useState('idle');
  const [period, setPeriod] = useState('1h');
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const nameInputRef = useRef(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [clients, setClients] = useState([]);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const fetchHistory = useCallback(async (p) => {
    setHistLoading(true);
    try {
      const ms = PERIODS.find(x => x.key === p)?.ms ?? 3600000;
      const from = new Date(Date.now() - ms).toISOString();
      const res = await api.get(`/boilers/${id}/history`, { params: { from, period: p } });
      setHistory(parseRows(res.data, p));
    } catch(e) {
      console.error('history fetch failed', e);
    } finally {
      setHistLoading(false);
    }
  }, [id]);

  useEffect(() => {
    api.get(`/boilers/${id}`).then((res) => {
      setBoiler(res.data);
      setAssignUserId(res.data.user_id ?? '');
      const t = res.data;
      setThresholds({
        temp_min: t.temp_min ?? 0,
        temp2_min: t.temp2_min ?? 0,
        temp2_max: t.temp2_max ?? 90,
        temp_max: t.temp_max ?? 90,
        pressure_min: t.pressure_min ?? 0.5,
        pressure_max: t.pressure_max ?? 3,
        notify_offline: t.notify_offline ?? true,
        notify_online: t.notify_online ?? true,
        notify_thresholds: t.notify_thresholds ?? true,
      });
    }).finally(() => setLoading(false));
    fetchHistory(period);
  }, [id, fetchHistory, period]);

  useEffect(() => {
    if (user?.role === 'admin') {
      api.get('/users').then((res) => setClients(res.data));
    }
  }, [user]);

  // Auto-refresh chart every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchHistory(period);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchHistory, period]);

  const handlePeriod = useCallback((p) => {
    setPeriod(p);
    fetchHistory(p);
  }, [fetchHistory]);

  useBoilerSocket(parseInt(id), (data) => {
    setBoiler((prev) => prev ? { ...prev, [data.field]: data.value, last_seen: data.timestamp } : prev);
  });

  const saveThresholds = async () => {
    setSaving(true);
    setThresholdSaveState('idle');
    try {
      await api.put(`/boilers/${id}/thresholds`, thresholds);
      setThresholdSaveState('saved');
      setTimeout(() => setThresholdSaveState('idle'), 2500);
    } catch (err) {
      console.error('Threshold save error:', err);
      setThresholdSaveState('error');
    } finally {
      setSaving(false);
    }
  };

  const startEditName = () => {
    setNameValue(boiler.name);
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/boilers/${id}`);
      navigate('/');
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const exportCSV = () => {
    if (!history.length) return;
    const header = 'Время;Темп. подача (°C);Темп. обратка (°C);Давление (бар)\n';
    const rows = history
      .filter(r => !r.gap)
      .map(r => `${r.time};${r.temperature ?? ''};${r.temperature2 ?? ''};${r.pressure ?? ''}`)
      .join('\n');
    const csv = header + rows;
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${boiler?.name || 'boiler'}_${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveAssign = async () => {
    setAssignSaving(true);
    try {
      await api.put(`/boilers/${id}`, {
        name: boiler.name, location: boiler.location, is_active: boiler.is_active,
        user_id: assignUserId || null,
      });
      setBoiler((prev) => ({ ...prev, user_id: assignUserId || null }));
    } finally {
      setAssignSaving(false);
    }
  };

  const saveName = async () => {
    if (!nameValue.trim() || nameValue === boiler.name) { setEditingName(false); return; }
    setNameSaving(true);
    try {
      await api.put(`/boilers/${id}`, { name: nameValue.trim(), location: boiler.location, is_active: boiler.is_active });
      setBoiler((prev) => ({ ...prev, name: nameValue.trim() }));
      setEditingName(false);
    } finally {
      setNameSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!boiler) return <div className="text-slate-400 text-center py-16">Котельная не найдена</div>;

  const tempWarn = boiler.temperature > thresholds.temp_max || boiler.temperature < thresholds.temp_min;
  const pressWarn = boiler.pressure > thresholds.pressure_max || boiler.pressure < thresholds.pressure_min;
  const isOnline = boiler.last_seen && (Date.now() - new Date(boiler.last_seen).getTime()) < OFFLINE_MS;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white p-1">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {editingName ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  ref={nameInputRef}
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  className="bg-slate-700 border border-blue-500 text-white rounded-lg px-3 py-1 text-base font-bold focus:outline-none flex-1 min-w-0"
                  style={{ fontSize: '16px' }}
                />
                <button onClick={saveName} disabled={nameSaving} className="text-emerald-400 hover:text-emerald-300 shrink-0 text-xs px-2 py-1 bg-emerald-500/10 rounded-lg">
                  {nameSaving ? '...' : 'OK'}
                </button>
                <button onClick={() => setEditingName(false)} className="text-slate-400 hover:text-white shrink-0 text-xs px-2 py-1 bg-slate-700 rounded-lg">✕</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-white truncate">{boiler.name}</h1>
                {user?.role === 'admin' && (
                  <button onClick={startEditName} className="text-slate-500 hover:text-slate-300 transition-colors shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
            )}
            <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
              isOnline ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-700 text-slate-400'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} />
              {isOnline ? 'Онлайн' : 'Оффлайн'}
            </span>
          </div>
          {boiler.location && <p className="text-slate-400 text-sm truncate">{boiler.location}</p>}
          {!isOnline && boiler.last_seen && (
            <p className="text-slate-500 text-xs mt-0.5">
              Последний сигнал: {new Date(boiler.last_seen).toLocaleString('ru-RU')}
            </p>
          )}
        </div>
      </div>

      {/* Current readings */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <StatCard label="Темп. на подаче" value={boiler.temperature} unit="°C" warn={tempWarn} offline={!isOnline} />
        <StatCard label="Давление" value={boiler.pressure} unit="бар" warn={pressWarn} offline={!isOnline} />
        <StatCard label="Темп. на обратке" value={boiler.temperature2} unit="°C" offline={!isOnline} />
      </div>

      {/* Period selector */}
      <div className="flex gap-1.5 mb-4">
        {PERIODS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handlePeriod(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              period === key
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'
            }`}
          >
            {label}
          </button>
        ))}
        {histLoading && <span className="text-slate-500 text-xs self-center ml-1">загрузка...</span>}
        <button
          onClick={exportCSV}
          disabled={!history.length}
          className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          CSV
        </button>
      </div>

      {/* Charts */}
      <BoilerChart data={history} dataKey="temperature" color="#ef4444" unit="°C" label="Температура на подаче, °C" gradientId="gradTemp" />
      <BoilerChart data={history} dataKey="temperature2" color="#3b82f6" unit="°C" label="Температура на обратке, °C" gradientId="gradTemp2" />
      <BoilerChart data={history} dataKey="pressure" color="#10b981" unit="бар" label="Среднее давление" gradientId="gradPres" secondaryDataKey="pressureMax" secondaryLabel="Максимальное давление" />

      {/* Thresholds + Notifications */}
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-4">
        <h2 className="font-semibold text-white mb-4">Пороговые значения</h2>
        <div className="grid grid-cols-2 gap-3 mb-5">
          {[
            { key: 'temp_min',      label: 'Подача мин (°C)' },
            { key: 'temp_max',      label: 'Подача макс (°C)' },
            { key: 'temp2_min',     label: 'Обратка мин (°C)' },
            { key: 'temp2_max',     label: 'Обратка макс (°C)' },
            { key: 'pressure_min',  label: 'Давл. мин (бар)' },
            { key: 'pressure_max',  label: 'Давл. макс (бар)' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs text-slate-400 mb-1">{label}</label>
              <input
                type="number"
                step="0.1"
                value={thresholds[key]}
                onChange={(e) => setThresholds((prev) => ({ ...prev, [key]: parseFloat(e.target.value) }))}
                className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" style={{fontSize: '16px'}}
              />
            </div>
          ))}
        </div>

        <div className="border-t border-slate-700 pt-4 mb-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Уведомления</h3>
          <div className="space-y-3">
            {[
              { key: 'notify_offline',    label: 'Котельная ушла в оффлайн', icon: '🔴' },
              { key: 'notify_online',     label: 'Котельная вернулась онлайн', icon: '🟢' },
              { key: 'notify_thresholds', label: 'Выход за пороги темп/давления', icon: '⚠️' },
            ].map(({ key, label, icon }) => (
              <label key={key} className="flex items-center gap-3 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={thresholds[key]}
                    onChange={(e) => setThresholds((prev) => ({ ...prev, [key]: e.target.checked }))}
                  />
                  <div className={`w-10 h-6 rounded-full transition-colors ${
                    thresholds[key] ? 'bg-blue-600' : 'bg-slate-600'
                  }`} />
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    thresholds[key] ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </div>
                <span className="text-sm text-slate-300 group-hover:text-white transition-colors">
                  {icon} {label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={saveThresholds}
          disabled={saving}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg py-2.5 text-sm transition-colors"
        >
          {saving ? 'Сохранение...' : thresholdSaveState === 'saved' ? 'Сохранено ✓' : 'Сохранить'}
        </button>
        {thresholdSaveState === 'error' && (
          <p className="text-red-400 text-xs text-center mt-2">Не удалось сохранить. Попробуйте ещё раз.</p>
        )}
      </div>

      <p className="text-center text-xs text-slate-600 mt-4 mb-2">
        {boiler.client_id} / {boiler.boiler_id}
      </p>

      {user?.role === 'admin' && (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-4 mb-4">
          <p className="text-sm font-semibold text-slate-300 mb-3">Привязка к клиенту</p>
          <div className="flex flex-col sm:flex-row gap-2 min-w-0">
            <select
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              className="w-full min-w-0 sm:flex-1 bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="">Без клиента (тестовая)</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.email || c.phone})</option>
              ))}
            </select>
            <button
              onClick={saveAssign}
              disabled={assignSaving}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              {assignSaving ? '...' : 'Сохранить'}
            </button>
          </div>
        </div>
      )}

      {user?.role === 'admin' && (
        <div className="mb-8">
          {confirmDelete ? (
            <div className="flex items-center justify-between bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              <span className="text-sm text-red-400">Удалить котельную навсегда?</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={handleDelete}
                  className="text-xs px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                >
                  Удалить
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              className="w-full flex items-center justify-center gap-2 border border-slate-700 hover:border-red-500/40 hover:bg-red-500/5 text-slate-500 hover:text-red-400 rounded-xl py-2.5 text-sm transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Удалить котельную
            </button>
          )}
        </div>
      )}
    </div>
  );
}
